import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarDays, MapPin, DollarSign, Trash2, Heart, Eye, ChevronDown, ChevronUp, ExternalLink, HelpCircle, Plus } from "lucide-react";
import { UnifiedFilterBar } from "@/components/unified-filter-bar";
import { formatDate } from "@/modules/calculator";
import type { MarketWithShows, ShowWithDetails } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import ShowDetail from "@/components/show-detail";
import { ShowListItem } from "@/components/show-list-item";
import { ShowListHeader } from "@/components/show-list-header";
import { useAuth } from "@/lib/auth";
import { usePersona } from "@/contexts/persona-context";
import { PlanStorage, type PackageTier } from "@/lib/plan-storage";
import { usePlanStore } from "@/stores/plan-store";
import { usePlanSidebar } from "@/contexts/plan-sidebar-context";
import { BrowseLayout } from "@/components/layouts/browse-layout";
import { calculateAudienceMatch } from "@/lib/audience-matching";
import { PRICING_MULTIPLIERS, SET_CORE_FLOOR_PRICE_PER_SHOW, type PackageType, getPackageConfig, formatPrice } from "@/lib/package-config";
import { trimShowsByFilters, type ShowFilterConfig } from '@shared/filter-engine';
import { DetailPageLoading, DetailPageError, StickyPricingBar, PricingSection, ShowDetailModal } from "@/components/detail-pages";

export default function MarketDetailPage() {
  const { id } = useParams();
  const [, setLocation] = useLocation();
  
  const { user } = useAuth();
  const { activePersona } = usePersona();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedShows, setSelectedShows] = useState<Set<number>>(new Set());
  const [viewingShow, setViewingShow] = useState<ShowWithDetails | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showStickyPricing, setShowStickyPricing] = useState(false);
  const { open: openPlanSidebar } = usePlanSidebar();
  const [expandedPackage, setExpandedPackage] = useState<PackageType | null>(null);
  const [calculatorPricing, setCalculatorPricing] = useState<{
    setVideo: number;
    setAudio: number;
    setCore: number;
    showCount: number;
  } | null>(null);
  const [isCalculatingPricing, setIsCalculatingPricing] = useState(false);
  const pricingRef = useRef<HTMLDivElement>(null);
  const pricingCardsRef = useRef<HTMLDivElement>(null);
  const hasAutoSelectedRef = useRef<string | null>(null); // Track if we've auto-selected for this market

  // Subscribe only to planPackages for backward compatibility (needed elsewhere in component)
  const planPackages = usePlanStore(state => state.getPackages(user?.id));

  // Helper functions for image handling
  const getImageSrc = () => {
    return market?.imageUrl || "https://max.live/hubfs/inventory/markets/default-market.png";
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    if (img.src !== "https://max.live/hubfs/inventory/markets/default-market.png") {
      img.src = "https://max.live/hubfs/inventory/markets/default-market.png";
    }
  };

  // Helper function to toggle package dropdown
  const togglePackageDropdown = (packageType: PackageType) => {
    setExpandedPackage(expandedPackage === packageType ? null : packageType);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (expandedPackage && !target.closest('.package-dropdown-container')) {
        setExpandedPackage(null);
      }
    };

    if (expandedPackage) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedPackage]);

  // Fetch market data
  const { data: market, isLoading: isMarketLoading, error } = useQuery({
    queryKey: [`/api/markets/${id}/with-shows`],
    queryFn: async () => {
      const response = await apiRequest(`/api/markets/${id}/with-shows`);
      return response as MarketWithShows;
    },
    enabled: !!id,
    staleTime: 0, // Force refetch
    gcTime: 0, // Don't cache
  });

  // Stage 2: Fetch demographics for artists when persona is active
  const artistIdsForDemographics = useMemo(() => {
    if (!market?.shows?.length || !activePersona) return [];
    const ids = new Set<number>();
    for (const show of market.shows) {
      if ((show.artist as any)?.id) ids.add((show.artist as any).id);
    }
    return Array.from(ids);
  }, [market?.shows, activePersona]);

  const { data: artistDemographics, isLoading: isDemographicsLoading } = useQuery({
    queryKey: ["/api/artists/demographics", artistIdsForDemographics],
    queryFn: async () => {
      if (artistIdsForDemographics.length === 0) return [];
      const response = await fetch('/api/artists/demographics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artistIds: artistIdsForDemographics }),
      });
      if (!response.ok) throw new Error('Failed to fetch artist demographics');
      return response.json();
    },
    enabled: artistIdsForDemographics.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const demographicsMap = useMemo(() => {
    if (!artistDemographics) return new Map();
    const map = new Map();
    for (const d of artistDemographics) { map.set(d.id, d); }
    return map;
  }, [artistDemographics]);

  const isLoading = isMarketLoading || (artistIdsForDemographics.length > 0 && isDemographicsLoading);

  // Reset state when market changes
  useEffect(() => {
    setViewingShow(null);
    setSelectedShows(new Set());
    setIsDescriptionExpanded(false);
    setShowStickyPricing(false);
    hasAutoSelectedRef.current = null; // Reset auto-selection tracking
  }, [id]);

  // Auto-select shows that are already in the plan (only on initial load)
  useEffect(() => {
    if (market?.shows && user?.id && market.id && hasAutoSelectedRef.current !== market.id.toString()) {
      const showsInPlan = new Set<number>();

      market.shows.forEach(show => {
        if (PlanStorage.isShowInPlan(show.id, user.id)) {
          showsInPlan.add(show.id);
        }
      });

      // Auto-select plan shows and mark that we've done this for this market
      if (showsInPlan.size > 0) {
        setSelectedShows(showsInPlan);
        hasAutoSelectedRef.current = market.id.toString();
      }
    }
  }, [market?.shows, market?.id, user?.id]);

  // Handle scroll to show/hide sticky pricing
  useEffect(() => {
    const handleScroll = () => {
      if (pricingCardsRef.current && selectedShows.size > 0) {
        const rect = pricingCardsRef.current.getBoundingClientRect();
        // Show sticky when the bottom of pricing cards goes out of view
        setShowStickyPricing(rect.bottom < 0);
      } else if (selectedShows.size === 0) {
        setShowStickyPricing(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [selectedShows.size]);

  // Effect to call calculator API when selected shows change
  useEffect(() => {
    const calculatePricing = async () => {
      if (selectedShows.size === 0 || !market?.shows || market.shows.length === 0) {
        setCalculatorPricing(null);
        return;
      }
      
      const selectedShowsList = market.shows.filter((show: any) => selectedShows.has(show.id));
      
      if (selectedShowsList.length === 0) {
        setCalculatorPricing(null);
        return;
      }
      
      setIsCalculatingPricing(true);
      
      try {
        // Group shows by artist for pricing calculation
        const artistGroups = new Map<number, ShowWithDetails[]>();
        selectedShowsList.forEach(show => {
          const artistId = show.artist.id;
          if (!artistGroups.has(artistId)) {
            artistGroups.set(artistId, []);
          }
          artistGroups.get(artistId)!.push(show);
        });

        let totalSetCore = 0;
        let totalSetAudio = 0;
        let totalSetVideo = 0;
        let totalShowCount = 0;

        // Calculate pricing for each artist group
        for (const [artistId, artistShows] of artistGroups) {
          const artist = artistShows[0].artist;
          const avgCapacity = artistShows.reduce((sum: number, show: any) => sum + (show.venue.capacity || 1000), 0) / artistShows.length;
          const showCount = artistShows.length;
          
          // Get market size from first show's venue or the market itself
          const firstShow = artistShows[0];
          const dmaPopulation = firstShow?.venue?.market?.population || market?.population;
          
          const getMarketSize = (dmaPopulation?: number | null) => {
            if (!dmaPopulation) return 'Medium';
            if (dmaPopulation >= 2000000) return 'Large';
            if (dmaPopulation >= 700000) return 'Medium';
            return 'Small';
          };
          
          const marketSize = getMarketSize(dmaPopulation);
          // Normalize career stage for calculator API
          const rawCareerStage = artist.careerStage || 'Established 2';
          const careerStage = rawCareerStage.replace(/Break-[Tt]hrough\//i, '');

          const requestBody = {
            careerStage,
            numberOfShows: showCount,
            mediaObjective: 'No Media',
            marketSize,
            venueCapacity: Math.round(avgCapacity),
          };
          
          // Call the calculator API once and get all package prices
          const data = await apiRequest('/api/calculator/calculate', {
            method: 'POST',
            body: requestBody,
          });
          
          // Use package pricing from server
          const packagePricing = data?.packagePricing;
          if (packagePricing) {
            totalSetCore += packagePricing.setCore;
            totalSetAudio += packagePricing.setAudio;
            totalSetVideo += packagePricing.setVideo;
            totalShowCount += showCount;
          }
        }
        
        if (totalSetVideo > 0) {
          const pricing = {
            setVideo: totalSetVideo,
            setAudio: totalSetAudio,
            setCore: totalSetCore,
            showCount: totalShowCount
          };
          setCalculatorPricing(pricing);
        } else {
          setCalculatorPricing(null);
        }
      } catch (error) {
        console.error('Error calculating pricing:', error);
        setCalculatorPricing(null);
      } finally {
        setIsCalculatingPricing(false);
      }
    };
    
    calculatePricing();
  }, [selectedShows, market?.shows, market]);

  // Read URL parameters for filtering
  const urlFilters = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const careerStageParam = urlParams.get('careerStage');
    const venueTypeParam = urlParams.get('venueType');
    return {
      location: urlParams.get('location') || '',
      genre: urlParams.get('genre') || '',
      dateFrom: urlParams.get('dateFrom') || '',
      dateTo: urlParams.get('dateTo') || '',
      artistName: urlParams.get('artistName') || '',
      venueName: urlParams.get('venueName') || '',
      careerStage: careerStageParam ? careerStageParam.split(',') : [],
      venueType: venueTypeParam ? venueTypeParam.split(',') : [],
      setArtist: urlParams.get('setArtist') || '',
    };
  }, []);

  // Check if there are any active filters to display
  const hasActiveFilters = useMemo(() => {
    return !!(urlFilters.genre || urlFilters.location || urlFilters.dateFrom || urlFilters.dateTo || urlFilters.artistName || urlFilters.venueName || (urlFilters.careerStage && urlFilters.careerStage.length > 0) || (urlFilters.venueType && urlFilters.venueType.length > 0) || activePersona);
  }, [urlFilters, activePersona]);

  // Apply URL parameter filtering and persona-based filtering
  const filteredAndSortedShows = useMemo(() => {
    if (!market?.shows) return [];

    // Merge demographics into artist data if available
    let shows = market.shows.map(show => {
      if (demographicsMap.size > 0) {
        const demo = demographicsMap.get((show.artist as any)?.id);
        if (demo) {
          return { ...show, artist: { ...show.artist, ...demo } };
        }
      }
      return show;
    });

    // Apply all URL parameter filters + exclude past shows in a single pass
    const showConfig: ShowFilterConfig = {
      dateFrom: urlFilters.dateFrom || undefined,
      dateTo: urlFilters.dateTo || undefined,
      genre: urlFilters.genre || undefined,
      location: urlFilters.location || undefined,
      artistName: urlFilters.artistName || undefined,
      venueName: urlFilters.venueName || undefined,
      careerStage: urlFilters.careerStage && urlFilters.careerStage.length > 0 ? urlFilters.careerStage : undefined,
      venueType: urlFilters.venueType && urlFilters.venueType.length > 0 ? urlFilters.venueType : undefined,
    };
    shows = trimShowsByFilters(shows, showConfig);

    // Apply persona-based filtering if active persona exists
    if (activePersona && user) {
      const hasPersonaFilters = Object.values({
        fanGender: activePersona.genders || [],
        fanEthnicity: activePersona.ethnicities || [],
        fanAge: activePersona.ageRanges || [],
        fanIncome: activePersona.incomeLevels || [],
        fanAttributes: activePersona.lifestyleAttributes || [],
      }).some(arr => arr.length > 0);

      if (hasPersonaFilters) {
        const targetAudience = {
          fanGender: activePersona.genders || [],
          fanEthnicity: activePersona.ethnicities || [],
          fanAge: activePersona.ageRanges || [],
          fanIncome: activePersona.incomeLevels || [],
          fanAttributes: activePersona.lifestyleAttributes || [],
        };

        // Calculate audience match for each show and filter by minimum match percentage
        // Only filter if artist has demographic data (from FULL query)
        shows = shows.filter(show => {
          const artist = show.artist as any;
          const hasArtistDemographics = artist?.fanGender || artist?.fanEthnicity ||
                                         artist?.fanAge || artist?.fanIncome || artist?.fanAttributes;

          // If artist has no demographic data (LIGHTWEIGHT query), skip filtering
          // to avoid false 0% matches that would filter out all shows
          if (!hasArtistDemographics) {
            return true;
          }

          const audienceMatch = calculateAudienceMatch(show.artist, targetAudience);
          const minPercentage = Math.max(1, activePersona.minMatchPercentage || 0);
          return audienceMatch.percentage >= minPercentage;
        });

        // Sort by audience match percentage (highest first)
        // Only sort if there's demographic data to sort by
        const hasAnyDemographicData = shows.some(show => {
          const artist = show.artist as any;
          return artist?.fanGender || artist?.fanEthnicity ||
                 artist?.fanAge || artist?.fanIncome || artist?.fanAttributes;
        });

        if (hasAnyDemographicData) {
          shows.sort((a, b) => {
            const matchA = calculateAudienceMatch(a.artist, targetAudience);
            const matchB = calculateAudienceMatch(b.artist, targetAudience);
            if (matchB.percentage !== matchA.percentage) {
              return matchB.percentage - matchA.percentage;
            }
            // Secondary sort by date (soonest first), then artist name
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            if (dateA !== dateB) return dateA - dateB;
            const nameA = (a.artist?.name || '').toLowerCase();
            const nameB = (b.artist?.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });

          // SET Artist hard filter - applied LAST to ensure it's never bypassed
          if (urlFilters.setArtist) {
            shows = shows.filter(show => {
              const artist = show.artist as any;
              const isSetArtist = artist?.setArtistGroupId != null && artist?.setArtistGroupId !== '';
              return urlFilters.setArtist === 'yes' ? isSetArtist : !isSetArtist;
            });
          }
          return shows;
        }
      }
    }

    // SET Artist hard filter - applied LAST to ensure it's never bypassed (default path)
    if (urlFilters.setArtist) {
      shows = shows.filter(show => {
        const artist = show.artist as any;
        const isSetArtist = artist?.setArtistGroupId != null && artist?.setArtistGroupId !== '';
        return urlFilters.setArtist === 'yes' ? isSetArtist : !isSetArtist;
      });
    }

    // Default sort: date ascending, then artist name
    return shows.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      if (dateA !== dateB) return dateA - dateB;
      const nameA = (a.artist?.name || '').toLowerCase();
      const nameB = (b.artist?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [market?.shows, activePersona, user, urlFilters, demographicsMap]);

  // Helper function to get audience match data for a show when persona is active
  const getAudienceMatchData = (show: ShowWithDetails) => {
    if (!activePersona || !user) return { percentage: 0, matchingAttributes: [] };
    
    const hasPersonaFilters = Object.values({
      fanGender: activePersona.genders || [],
      fanEthnicity: activePersona.ethnicities || [],
      fanAge: activePersona.ageRanges || [],
      fanIncome: activePersona.incomeLevels || [],
      fanAttributes: activePersona.lifestyleAttributes || [],
    }).some(arr => arr.length > 0);

    if (!hasPersonaFilters) return { percentage: 0, matchingAttributes: [] };

    const targetAudience = {
      fanGender: activePersona.genders || [],
      fanEthnicity: activePersona.ethnicities || [],
      fanAge: activePersona.ageRanges || [],
      fanIncome: activePersona.incomeLevels || [],
      fanAttributes: activePersona.lifestyleAttributes || [],
    };

    return calculateAudienceMatch(show.artist, targetAudience);
  };

  // Use filtered shows for all calculations (moved before early returns to fix hooks ordering)
  const displayShows = filteredAndSortedShows;
  const sponsorableShows = displayShows.filter(show => show.sponsorable ?? true);
  const selectedCount = selectedShows.size;
  const allSponsorableSelected = sponsorableShows.length > 0 && sponsorableShows.every(show => selectedShows.has(show.id));

  // Calculate package counts - read directly from store without subscription (moved before early returns to fix hooks ordering)
  const packageCounts = useMemo(() => {
    const selectedShowIds = Array.from(selectedShows);

    // Read directly from store state (no subscription)
    const state = usePlanStore.getState();
    const userKey = user?.id ?? 0;
    const planShowsObj = state.shows[userKey] || {};
    const planPackagesObj = state.packages[userKey] || {};

    const showIdsInPlan = Object.keys(planShowsObj).map(Number);
    const inPlanShowIds = new Set(showIdsInPlan);

    // Shows selected but not yet in plan
    const newlySelectedShowIds = selectedShowIds.filter(id => !inPlanShowIds.has(id));
    const newlySelectedCount = newlySelectedShowIds.length;

    // Count committed shows by tier - ONLY for shows in the current market
    // Create set of show IDs that belong to this market
    const marketShowIds = new Set(market?.shows?.map(s => s.id) || []);

    let setCoreCommitted = 0;
    let setAudioCommitted = 0;
    let setVideoCommitted = 0;

    showIdsInPlan.forEach(showId => {
      // Only count this show if it belongs to the current market
      if (marketShowIds.has(showId)) {
        const tier = planPackagesObj[showId]?.tier || 'set-video';
        if (tier === 'set-core') setCoreCommitted++;
        else if (tier === 'set-audio') setAudioCommitted++;
        else setVideoCommitted++;
      }
    });

    return {
      'set-core': {
        committed: setCoreCommitted,
        selected: newlySelectedCount,
        total: setCoreCommitted + newlySelectedCount
      },
      'set-audio': {
        committed: setAudioCommitted,
        selected: newlySelectedCount,
        total: setAudioCommitted + newlySelectedCount
      },
      'set-video': {
        committed: setVideoCommitted,
        selected: newlySelectedCount,
        total: setVideoCommitted + newlySelectedCount
      }
    };
  }, [selectedShows, user?.id, market?.id, market?.shows]);

  const handleAddToProposal = (show: ShowWithDetails) => {
    const wasAdded = PlanStorage.addShow(show, user?.id);
    
    if (wasAdded) {
      toast({
        title: "Added to Proposal",
        description: `${show.artist.name} show on ${formatDate(show.date)} added to your proposal.`,
      });
    } else {
      toast({
        title: "Already in Proposal",
        description: `${show.artist.name} show on ${formatDate(show.date)} is already in your proposal.`,
        variant: "destructive",
      });
    }
  };

  const handleBulkAddToProposal = (shows: ShowWithDetails[]) => {
    let addedCount = 0;
    shows.forEach(show => {
      const wasAdded = PlanStorage.addShow(show, user?.id);
      if (wasAdded) addedCount++;
    });
    
    if (addedCount > 0) {
      toast({
        title: "Added to Proposal",
        description: `${addedCount} show(s) added to your proposal.`,
      });
    } else {
      toast({
        title: "Already in Proposal",
        description: "All selected shows are already in your proposal.",
        variant: "destructive",
      });
    }
  };

  const handleAddPackageToProposal = (packageTier: PackageTier, packagePrice: number) => {
    if (!calculatorPricing || selectedShows.size === 0) {
      toast({
        title: "No Shows Selected",
        description: "Please select shows before adding a package to your proposal.",
        variant: "destructive",
      });
      return;
    }

    const selectedShowsList = displayShows.filter((show: ShowWithDetails) => selectedShows.has(show.id));
    const pricePerShow = Math.round(packagePrice / selectedShowsList.length);
    
    const addedCount = PlanStorage.addShowsWithPackage(
      selectedShowsList,
      packageTier,
      pricePerShow,
      user?.id
    );
    
    if (addedCount > 0) {
      const tierLabel = packageTier === 'set-core' ? 'SET Core' : packageTier === 'set-audio' ? 'SET Audio' : 'SET Video';

      // Clear selection after adding to plan
      setSelectedShows(new Set());

      // Open the plan sidebar when shows are added
      openPlanSidebar();

      toast({
        title: "Added to Proposal",
        description: `${addedCount} show(s) added with ${tierLabel} package.`,
      });
    } else {
      toast({
        title: "Already in Proposal",
        description: "All selected shows are already in your proposal.",
        variant: "destructive",
      });
    }
  };

  const handleViewShow = (show: ShowWithDetails) => {
    setViewingShow(show);
  };

  const handleBackToList = () => {
    setViewingShow(null);
  };

  const handleShowSelection = (showId: number, checked: boolean) => {
    const newSelection = new Set(selectedShows);
    if (checked) {
      newSelection.add(showId);
    } else {
      newSelection.delete(showId);
    }
    setSelectedShows(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && market) {
      const sponsorableShowIds = displayShows.filter(show => show.sponsorable ?? true).map(show => show.id);
      setSelectedShows(new Set(sponsorableShowIds));
    } else {
      setSelectedShows(new Set());
    }
  };

  if (isLoading) {
    return <DetailPageLoading />;
  }

  if (error || !market) {
    return (
      <DetailPageError
        title="Market Not Found"
        message="The market you're looking for doesn't exist or has been removed."
        backHref="/browse-markets"
        backText="Back to Browse Markets"
      />
    );
  }

  // Don't return early for viewing show - we'll render it as a modal at the bottom

  // Format price helper
  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };


  // Use calculator pricing instead of manual calculation
  const calculateTotalPricing = calculatorPricing;

  return (
    <BrowseLayout>
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Sticky Pricing Bar */}
      <StickyPricingBar
        show={showStickyPricing && !!calculateTotalPricing}
        selectedCount={selectedShows.size}
        contextName={`in ${market?.name}`}
        pricing={calculateTotalPricing || { setCore: 0, setAudio: 0, setVideo: 0, showCount: 0 }}
        expandedPackage={expandedPackage}
        onTogglePackage={togglePackageDropdown}
        onAddPackage={(packageType, price) => handleAddPackageToProposal(packageType as PackageTier, price)}
      />
      {/* Sticky Filter Bar with Back Button */}
      <UnifiedFilterBar
        readOnly
        currentMethod="markets"
        filters={{
          location: urlFilters.location || '',
          genre: urlFilters.genre || '',
          dateFrom: urlFilters.dateFrom || '',
          dateTo: urlFilters.dateTo || '',
          artistName: urlFilters.artistName || '',
          venueName: urlFilters.venueName || '',
          careerStage: urlFilters.careerStage,
          venueType: urlFilters.venueType,
          setArtist: urlFilters.setArtist,
        }}
        backLink={`/browse-markets${window.location.search}`}
        backLabel="Back to Browse Markets"
      />

      {/* Market Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Market Image */}
          <div className="flex-shrink-0">
            <div className="w-48 h-48 bg-gradient-to-br from-blue-100 to-green-200 rounded-lg flex items-center justify-center overflow-hidden">
              <img
                src={getImageSrc()}
                alt={market.name}
                className="w-full h-full object-cover rounded-lg"
                onError={handleImageError}
              />
            </div>
          </div>

          {/* Market Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold mb-2">{market.name}</h1>
                {market.dmaCode && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge variant="secondary">DMA {market.dmaCode}</Badge>
                    {market.rank && <Badge variant="outline">Rank #{market.rank}</Badge>}
                    {market.marketSize && <Badge variant="outline">{market.marketSize} Market</Badge>}
                  </div>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-max-brand">{displayShows.length}</div>
                <div className="text-sm text-gray-600">{hasActiveFilters ? 'Matching Shows' : 'Total Shows'}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-max-brand">{sponsorableShows.length}</div>
                <div className="text-sm text-gray-600">Sponsorable</div>
              </div>
              {market.population && (
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-700">{market.population.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Population</div>
                </div>
              )}
              {market.majorCities && market.majorCities.length > 0 && (
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-700">{market.majorCities.length}</div>
                  <div className="text-sm text-gray-600">Major Cities</div>
                </div>
              )}
            </div>

            {/* Major Cities */}
            {market.majorCities && market.majorCities.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold mb-2">Major Cities</h3>
                <div className="flex flex-wrap gap-2">
                  {market.majorCities.slice(0, 6).map((city, index) => (
                    <Badge key={index} variant="outline" className="text-xs">{city}</Badge>
                  ))}
                  {market.majorCities.length > 6 && (
                    <Badge variant="outline" className="text-xs">+{market.majorCities.length - 6} more</Badge>
                  )}
                </div>
              </div>
            )}

            {/* States */}
            {market.states && market.states.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold mb-2">States</h3>
                <div className="flex flex-wrap gap-2">
                  {market.states.map((state, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">{state}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Demographics */}
        {market.demographics && Object.keys(market.demographics).length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Market Demographics</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              >
                {isDescriptionExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
            {isDescriptionExpanded && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {Object.entries(market.demographics).map(([key, value]) => (
                  <div key={key}>
                    <div className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                    <div className="text-gray-600">{String(value)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dynamic Pricing Section */}
      <PricingSection
        user={user}
        sponsorableShowsCount={sponsorableShows.length}
        contextName={`in ${market?.name}`}
        pricing={calculatorPricing}
        isCalculating={isCalculatingPricing}
        selectedCount={selectedCount}
        packageCounts={packageCounts}
        onAddPackage={(packageType, price) => handleAddPackageToProposal(packageType as PackageTier, price)}
        pricingCardsRef={pricingCardsRef}
      />

      {/* Shows Section */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">
            {hasActiveFilters ? `Matching Shows in ${market.name}` : `Shows in ${market.name}`}
          </h2>
        </div>

        {displayShows.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">
              {hasActiveFilters ?
                'No shows match your current filter criteria.' :
                'No shows scheduled in this market.'
              }
            </p>
          </div>
        ) : (
          <>
            {user && sponsorableShows.length > 0 && (
              <ShowListHeader
                title="Shows"
                totalShows={displayShows.length}
                sponsorableShows={sponsorableShows.length}
                selectedCount={selectedCount}
                allSponsorableSelected={allSponsorableSelected}
                onSelectAll={handleSelectAll}
                isAuthenticated={true}
              />
            )}
            
            <div className="space-y-3">
              {displayShows.map((show) => {
                const audienceMatch = getAudienceMatchData(show);
                const hasPersonaFilters = activePersona && user && Object.values({
                  fanGender: activePersona.genders || [],
                  fanEthnicity: activePersona.ethnicities || [],
                  fanAge: activePersona.ageRanges || [],
                  fanIncome: activePersona.incomeLevels || [],
                  fanAttributes: activePersona.lifestyleAttributes || [],
                }).some(arr => arr.length > 0);
                
                // Check if show is in plan - read from reactive store
                const packageInfo = planPackages[show.id];
                const isInPlan = !!packageInfo;
                
                return (
                  <ShowListItem
                    key={show.id}
                    show={show}
                    isSelectable={show.sponsorable ?? true}
                    isSelected={selectedShows.has(show.id)}
                    onSelectionChange={(showId, checked) => handleShowSelection(showId, checked)}
                    onViewShow={() => handleViewShow(show)}
                    isAuthenticated={!!user}
                    showArtistInfo={true}
                    showVenueInfo={true}
                    // Audience matching props when persona is active
                    audienceMatchPercentage={hasPersonaFilters ? audienceMatch.percentage : undefined}
                    matchingAttributes={hasPersonaFilters ? audienceMatch.matchingAttributes : undefined}
                    showAudienceMatch={hasPersonaFilters ?? false}
                    // Plan status props
                    isInPlan={isInPlan}
                    planPackageTier={packageInfo?.tier}
                    planPackagePrice={packageInfo?.price}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Show Detail Modal */}
      <ShowDetailModal
        show={viewingShow}
        onBack={handleBackToList}
        onAddToProposal={handleAddToProposal}
        isAuthenticated={!!user}
        backToText={`Back to ${market?.name}`}
      />
    </div>
    </BrowseLayout>
  );
}